import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { FormBuilder, FormGroup, Validators, FormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { debounceTime, distinctUntilChanged, switchMap, startWith } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { AuthService } from '../components/services/auth.service';
import { BookingService } from '../components/services/booking.service';
import { environment } from '../environments/environment';

interface RentalEquipment {
  id: number;
  name: string;
  category: string;
  dailyRate: number;
  rating: number;
  provider: string;
  description: string;
  imageUrl?: string;
}

@Component({
  selector: 'app-equipment-rentals',
  templateUrl: './equipment-rentals.component.html',
  styleUrls: ['./equipment-rentals.component.css']
})
export class EquipmentRentalsComponent implements OnInit {
  equipment: RentalEquipment[] = [];
  filteredEquipment: RentalEquipment[] = [];
  allEquipment: RentalEquipment[] = [];

  searchControl = new FormControl('');
  categoryControl = new FormControl('');
  priceFilter = 'all';
  minRating = 0;
  sortBy = 'relevance';
  page = 1;
  pageSize = 9;
  totalPages = 1;
  loading = true;

  // Rental request modal state
  showRentalModal = false;
  selectedEquipment: RentalEquipment | null = null;
  rentalForm: FormGroup;
  isSubmitting = false;
  rentalSuccess = false;
  rentalError: string | null = null;
  rentalDays = 0;

  private searchSubject = new Subject<string>();

  categories = [
    { value: 'excavator', label: 'Excavators' },
    { value: 'generator', label: 'Generators' },
    { value: 'scaffolding', label: 'Scaffolding' },
    { value: 'concrete-mixer', label: 'Concrete Mixers' },
    { value: 'crane', label: 'Cranes' },
    { value: 'forklift', label: 'Forklifts' },
    { value: 'power-tools', label: 'Power Tools' },
    { value: 'compactor', label: 'Compactors' }
  ];

  constructor(
    private http: HttpClient,
    private fb: FormBuilder,
    private authService: AuthService,
    private bookingService: BookingService,
    private router: Router
  ) {
    this.rentalForm = this.fb.group({
      startDate: ['', Validators.required],
      endDate: ['', Validators.required],
      deliveryAddress: ['', Validators.required],
      notes: ['']
    });
  }

  ngOnInit() {
    this.loadEquipment();

    const user = this.authService.getCurrentUser();
    if (user) {
      this.rentalForm.patchValue({ deliveryAddress: (user as any).address || '' });
    }

    this.searchControl.valueChanges
      .pipe(startWith(''), debounceTime(300), distinctUntilChanged())
      .subscribe(term => this.searchSubject.next(term || ''));

    this.searchSubject
      .pipe(switchMap(term => this.searchEquipment(term)))
      .subscribe(results => {
        this.allEquipment = results;
        this.applyFilters();
      });

    this.rentalForm.get('startDate')?.valueChanges.subscribe(() => this.updateRentalDays());
    this.rentalForm.get('endDate')?.valueChanges.subscribe(() => this.updateRentalDays());
  }

  loadEquipment() {
    this.loading = true;
    this.http.get<RentalEquipment[]>(environment.apiUrl + 'EquipmentRentals')
      .subscribe({
        next: (data) => {
          this.allEquipment = this.withImage(data);
          this.applyFilters();
          this.loading = false;
        },
        error: () => {
          this.allEquipment = this.withImage(this.getMockRentalEquipment());
          this.applyFilters();
          this.loading = false;
        }
      });
  }

  private withImage(data: RentalEquipment[]): RentalEquipment[] {
    return data.map(e => ({
      ...e,
      imageUrl: e.imageUrl || this.pickImageForEquipment(e.category),
      description: e.description || 'No description provided.',
      provider: e.provider || (e as any).providerName || 'Unknown Provider'
    }));
  }

  private categoryTags: { [key: string]: string } = {
    excavator: 'excavator,heavyequipment',
    generator: 'generator,powerequipment',
    scaffolding: 'scaffolding,construction',
    'concrete-mixer': 'cementmixer,construction',
    crane: 'crane,constructionsite',
    forklift: 'forklift,warehouse',
    'power-tools': 'powertools,construction',
    compactor: 'compactor,pavement'
  };

  // LoremFlickr needs no API key and returns real Flickr photos for a tag;
  // `lock` pins the result so the same listing always gets the same photo.
  private pickImageForEquipment(category: string): string {
    const tag = this.categoryTags[category] || 'construction,equipment';
    const lock = this.hashTitle(category + Math.random());
    return `https://loremflickr.com/400/300/${tag}/all?lock=${lock}`;
  }

  private hashTitle(title: string): number {
    let hash = 0;
    for (let i = 0; i < title.length; i++) {
      hash = (hash * 31 + title.charCodeAt(i)) >>> 0;
    }
    return hash % 1000;
  }

  searchEquipment(term: string) {
    const base = environment.apiUrl + 'EquipmentRentals';
    if (!term.trim()) return this.http.get<RentalEquipment[]>(base);
    return this.http.get<RentalEquipment[]>(`${base}?search=${encodeURIComponent(term)}`);
  }

  applyFilters() {
    let filtered = [...this.allEquipment];
    const term = this.searchControl.value?.toLowerCase() || '';
    if (term) {
      filtered = filtered.filter(e =>
        e.name.toLowerCase().includes(term) ||
        e.description.toLowerCase().includes(term) ||
        e.provider.toLowerCase().includes(term)
      );
    }
    if (this.categoryControl.value) {
      filtered = filtered.filter(e => e.category === this.categoryControl.value);
    }
    if (this.priceFilter === 'under100')     filtered = filtered.filter(e => e.dailyRate < 100);
    else if (this.priceFilter === '100to300') filtered = filtered.filter(e => e.dailyRate >= 100 && e.dailyRate <= 300);
    else if (this.priceFilter === 'over300')  filtered = filtered.filter(e => e.dailyRate > 300);
    if (this.minRating > 0) filtered = filtered.filter(e => e.rating >= this.minRating);

    this.equipment = filtered;
    this.sortEquipment();
  }

  sortEquipment() {
    const data = [...this.equipment];
    if (this.sortBy === 'price-low')  data.sort((a, b) => a.dailyRate - b.dailyRate);
    else if (this.sortBy === 'price-high') data.sort((a, b) => b.dailyRate - a.dailyRate);
    else if (this.sortBy === 'rating') data.sort((a, b) => b.rating - a.rating);
    this.equipment = data;
    this.updatePagination();
  }

  updatePagination() {
    const start = (this.page - 1) * this.pageSize;
    this.filteredEquipment = this.equipment.slice(start, start + this.pageSize);
    this.totalPages = Math.ceil(this.equipment.length / this.pageSize);
  }

  changePage(p: number) {
    this.page = p;
    this.updatePagination();
    window.scrollTo(0, 0);
  }

  resetFilters() {
    this.searchControl.setValue('');
    this.categoryControl.setValue('');
    this.priceFilter = 'all';
    this.minRating = 0;
    this.sortBy = 'relevance';
    this.page = 1;
    this.loadEquipment();
  }

  // ── Rental request ──────────────────────────────────────────

  rentEquipment(item: RentalEquipment, event: Event) {
    event.stopPropagation();
    this.rentalError = null;
    this.rentalSuccess = false;

    const user = this.authService.getCurrentUser();
    if (!user) {
      this.router.navigate(['/auth']);
      return;
    }

    this.selectedEquipment = item;
    this.rentalForm.reset({
      startDate: '',
      endDate: '',
      deliveryAddress: (user as any).address || '',
      notes: ''
    });
    this.rentalDays = 0;
    this.showRentalModal = true;
  }

  closeModal() {
    this.showRentalModal = false;
    this.selectedEquipment = null;
    this.rentalSuccess = false;
    this.rentalError = null;
  }

  updateRentalDays() {
    const { startDate, endDate } = this.rentalForm.value;
    if (!startDate || !endDate) {
      this.rentalDays = 0;
      return;
    }
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diff = Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    this.rentalDays = diff > 0 ? diff : 0;
  }

  get estimatedTotal(): number {
    return this.selectedEquipment ? this.selectedEquipment.dailyRate * this.rentalDays : 0;
  }

  submitRental() {
    if (this.rentalForm.invalid || this.rentalDays <= 0) {
      this.rentalForm.markAllAsTouched();
      return;
    }

    const user = this.authService.getCurrentUser();
    const item = this.selectedEquipment!;
    const { startDate, endDate, deliveryAddress, notes } = this.rentalForm.value;

    const rental = {
      customerId: (user as any)?.id,
      serviceId: String(item.id),
      serviceName: item.name,
      providerName: item.provider,
      scheduledDate: startDate,
      scheduledTime: '00:00',
      address: deliveryAddress,
      notes: `Rental ${startDate} to ${endDate}${notes ? ' — ' + notes : ''}`,
      price: this.estimatedTotal,
      status: 'pending' as const,
      isRecurring: false
    };

    this.isSubmitting = true;
    this.bookingService.createBooking(rental).subscribe({
      next: () => {
        this.isSubmitting = false;
        this.rentalSuccess = true;
      },
      error: (err) => {
        this.isSubmitting = false;
        this.rentalError = err?.error?.message || 'Could not submit this rental request. Please try again.';
      }
    });
  }

  get minDate(): string {
    return new Date().toISOString().split('T')[0];
  }

  private getMockRentalEquipment(): RentalEquipment[] {
    return [
      { id: 1, name: 'Mini Excavator (3-Ton)',            category: 'excavator',      dailyRate: 285, rating: 4.8, provider: 'BuildRite Equipment Rental',  description: 'Compact excavator ideal for tight-access digging, trenching, and landscaping jobs.' },
      { id: 2, name: 'Portable Diesel Generator (20kW)',   category: 'generator',      dailyRate: 145, rating: 4.7, provider: 'PowerPro Rentals',            description: 'Job-site power for tools, lighting, and temporary electrical needs.' },
      { id: 3, name: 'Scaffolding Tower Set (20ft)',       category: 'scaffolding',    dailyRate: 95,  rating: 4.6, provider: 'SafeReach Scaffolding',       description: 'Modular scaffold set with guardrails and locking casters, easy to assemble.' },
      { id: 4, name: 'Towable Concrete Mixer (6 cu ft)',   category: 'concrete-mixer', dailyRate: 80,  rating: 4.5, provider: 'MixMasters Rental Co',       description: 'Gas-powered towable mixer for slabs, footings, and small pours.' },
      { id: 5, name: 'Rough Terrain Telescopic Crane',     category: 'crane',          dailyRate: 620, rating: 4.9, provider: 'Elite Crane Services',        description: 'All-terrain crane with 80ft reach for framing, roofing, and material placement.' },
      { id: 6, name: 'Warehouse Forklift (5,000 lb)',      category: 'forklift',       dailyRate: 165, rating: 4.7, provider: 'HeavyLift Rentals',          description: 'Propane forklift for loading, unloading, and moving palletized materials.' },
      { id: 7, name: 'Power Tool Kit (Drills, Saws, Nailers)', category: 'power-tools', dailyRate: 55,  rating: 4.6, provider: 'ToolShed Rentals',         description: 'Full kit of cordless drills, circular saws, and framing nailers with batteries.' },
      { id: 8, name: 'Plate Compactor (Reversible)',       category: 'compactor',      dailyRate: 90,  rating: 4.5, provider: 'GroundWorks Equipment',      description: 'Reversible plate compactor for base prep, driveways, and paver installs.' },
      { id: 9, name: 'Track Skid Steer Loader',            category: 'excavator',      dailyRate: 310, rating: 4.8, provider: 'BuildRite Equipment Rental',  description: 'Compact track loader with bucket attachment for grading and material handling.' }
    ];
  }
}
